import { useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'
import { LanguageProvider, useLanguage } from '@/components/LanguageContext'
import { stripLanguagePrefix } from '@/lib/i18n'
import { LocalizedLink, useLocalizedPath } from '@/components/LocalizedLink'
import LanguageSelector from '@/components/LanguageSelector'
import { useDocumentMeta } from '@/hooks/useDocumentMeta'
import { ChatWidget } from '@/components/chat/ChatWidget'

// `external: true` força navegação full-page (fora do SPA) — o /blog é servido
// por um worker via rewrite no vercel.json, não por uma rota do react-router.
const navItems: { labelKey: string; path: string; external?: boolean }[] = [
  { labelKey: 'nav.home', path: '/' },
  { labelKey: 'nav.directory', path: '/diretorio' },
  { labelKey: 'nav.map', path: '/mapa' },
  { labelKey: 'nav.forum', path: '/forum' },
  { labelKey: 'nav.blog', path: '/blog', external: true },
  { labelKey: 'nav.innovation', path: '/inovacao' },
  { labelKey: 'nav.register', path: '/cadastrar' },
  { labelKey: 'nav.contact', path: '/contato' },
]

function PublicLayoutContent() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { t } = useLanguage()
  const location = useLocation()
  // Title/description/OG/hreflang + `<html lang>` por idioma e por rota
  // (Tarefa 10). Vive aqui porque este layout envolve TODA a superfície
  // pública -- uma chamada cobre as 10 rotas.
  useDocumentMeta()
  // O prefixo de idioma não muda a identidade da rota: `/ja` também é a home.
  const isHome = stripLanguagePrefix(location.pathname) === '/'
  // Links internos preservam o idioma corrente (em PT o resultado é idêntico
  // ao path original — a raiz continua sem prefixo). O helper é o MESMO que o
  // `LocalizedLink` usa: um lugar só decide como um link interno é montado.
  const localize = useLocalizedPath()

  return (
    <div className="min-h-screen bg-[#FAFAF5]">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isHome
            ? 'bg-transparent'
            : 'bg-white/95 backdrop-blur-md shadow-sm'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <LocalizedLink to="/" className="flex items-center gap-3">
              <img
                src="/logo.jpg"
                alt="AgroBio"
                width={44}
                height={44}
                className="h-11 w-11 rounded-full object-cover ring-2 ring-white/40"
              />
              <span className={`text-xl font-bold ${isHome ? 'text-white' : 'text-[#1B4332]'}`}>
                AgroBio
              </span>
            </LocalizedLink>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => {
                const isActive = !item.external && location.pathname === localize(item.path)
                const className = `font-medium transition-colors ${
                  isActive
                    ? isHome
                      ? 'text-white'
                      : 'text-[#40916C]'
                    : isHome
                      ? 'text-white/70 hover:text-white'
                      : 'text-gray-600 hover:text-[#40916C]'
                }`
                return item.external ? (
                  <a key={item.path} href={item.path} className={className}>
                    {t(item.labelKey)}
                  </a>
                ) : (
                  <LocalizedLink key={item.path} to={item.path} className={className}>
                    {t(item.labelKey)}
                  </LocalizedLink>
                )
              })}
              <LanguageSelector variant={isHome ? 'ghost' : 'outline'} />
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector variant={isHome ? 'ghost' : 'outline'} />
              <Button
                variant="ghost"
                size="icon"
                className={isHome ? 'text-white hover:bg-white/10' : 'text-gray-700'}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t"
            >
              <nav className="container mx-auto px-6 py-4 space-y-2">
                {navItems.map((item) => {
                  const isActive = !item.external && location.pathname === localize(item.path)
                  const className = `block py-3 px-4 rounded-xl font-medium transition-colors ${
                    isActive
                      ? 'bg-[#D8F3DC] text-[#40916C]'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`
                  return item.external ? (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={className}
                    >
                      {t(item.labelKey)}
                    </a>
                  ) : (
                    <LocalizedLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={className}
                    >
                      {t(item.labelKey)}
                    </LocalizedLink>
                  )
                })}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main content */}
      <main className={isHome ? '' : 'pt-20'}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#1B4332] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/logo.jpg"
                  alt="AgroBio"
                  width={44}
                  height={44}
                  className="h-11 w-11 rounded-full object-cover ring-2 ring-white/20"
                />
                <span className="text-xl font-bold">AgroBio</span>
              </div>
              <p className="text-[#B7E4C7]/70 max-w-md mb-6">
                {t('footer.description')}
              </p>

              <h4 className="font-semibold mb-4">{t('footer.downloadApp')}</h4>
              <div className="flex gap-4">
                <a
                  href="#"
                  className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs text-[#B7E4C7]/50">{t('footer.googlePlay')}</div>
                    <div className="text-sm font-semibold">Google Play</div>
                  </div>
                </a>
                <a
                  href="#"
                  className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.523 15.341c-.461.832-1.001 1.491-1.621 1.978-.619.487-1.316.731-2.091.731-.513 0-1.139-.145-1.879-.436-.74-.29-1.421-.436-2.043-.436-.641 0-1.34.15-2.098.451-.757.301-1.365.451-1.822.451-.794 0-1.537-.259-2.228-.776-.691-.518-1.263-1.19-1.717-2.017C1.445 14.498 1 13.168 1 11.611c0-1.699.414-3.088 1.243-4.168.829-1.08 1.877-1.62 3.145-1.62.679 0 1.527.21 2.545.631 1.018.42 1.667.631 1.948.631.224 0 .986-.263 2.286-.789 1.28-.506 2.36-.717 3.24-.632.85.076 1.57.289 2.161.639.591.349 1.088.822 1.492 1.419-.566.344-1.017.766-1.353 1.267-.336.5-.504 1.096-.504 1.788 0 .826.22 1.554.659 2.184.439.631.997 1.072 1.674 1.323-.175.526-.376 1.015-.602 1.468zm-5.474-13.655c0 .647-.193 1.262-.58 1.845-.386.583-.895 1.033-1.526 1.35-.105.053-.197.08-.276.08-.053 0-.097-.013-.132-.04-.035-.026-.053-.066-.053-.118.018-.647.233-1.254.646-1.821.413-.567.95-1.013 1.612-1.338.079-.039.145-.059.197-.059.053 0 .088.013.106.04.018.026.027.066.027.118-.001.065-.007.13-.021.195z" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs text-[#B7E4C7]/50">{t('footer.appStore')}</div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.navigation')}</h4>
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.path}>
                    {item.external ? (
                      <a
                        href={item.path}
                        className="text-[#B7E4C7]/70 hover:text-white transition-colors"
                      >
                        {t(item.labelKey)}
                      </a>
                    ) : (
                      <LocalizedLink
                        to={item.path}
                        className="text-[#B7E4C7]/70 hover:text-white transition-colors"
                      >
                        {t(item.labelKey)}
                      </LocalizedLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer.categories')}</h4>
              <ul className="space-y-2 text-[#B7E4C7]/70">
                <li>{t('footer.categoryList.bioinput')}</li>
                <li>{t('footer.categoryList.university')}</li>
                <li>{t('footer.categoryList.rural')}</li>
                <li>{t('footer.categoryList.startup')}</li>
                <li>{t('footer.categoryList.association')}</li>
                <li>{t('footer.categoryList.logistics')}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-12 pt-8 text-center text-[#B7E4C7]/50 text-sm">
            {t('footer.rights')}
          </div>
        </div>
      </footer>

      {/* Assistente de bioinsumos — widget flutuante global do portal público */}
      <ChatWidget />
    </div>
  )
}

export function PublicLayout() {
  return (
    <LanguageProvider>
      <PublicLayoutContent />
    </LanguageProvider>
  )
}
